import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { type AuthUser } from "wasp/auth";
import {
  getAdminBlogPosts,
  getBlogCategories,
  createBlogPost,
  updateBlogPost,
  publishBlogPost,
  unpublishBlogPost,
  createFileUploadUrl,
  addFileToDb,
  useQuery,
  useAction,
} from "wasp/client/operations";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import NotebookEditor from "../../../notebook/LazyNotebookEditor";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { Label } from "../../../client/components/ui/label";
import { Textarea } from "../../../client/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../client/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../client/components/ui/accordion";
import { useToast } from "../../../client/hooks/use-toast";
import { X, Upload } from "lucide-react";
import useTranslation from "../../../client/hooks/useTranslation";
import { ALLOWED_FILE_TYPES } from "../../../file-upload/validation";

type AllowedFileType = typeof ALLOWED_FILE_TYPES[number];

function toSlug(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

// SSR SEO body editing uses a simple line format ("clé | valeur" per line) so
// admins can edit H2 sections and internal links without a repeater UI.
// Sections: "Titre H2 | Paragraphe". Links: "/chemin | Libellé".
type SeoSection = { h2: string; text: string };
type SeoLink = { href: string; label: string };

function sectionsToText(sections: SeoSection[] | null | undefined): string {
  return (sections ?? []).map((s) => `${s.h2} | ${s.text}`).join("\n");
}
function textToSections(text: string): SeoSection[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const i = line.indexOf("|");
      if (i === -1) return { h2: line, text: "" };
      return { h2: line.slice(0, i).trim(), text: line.slice(i + 1).trim() };
    })
    .filter((s) => s.h2 && s.text);
}
function linksToText(links: SeoLink[] | null | undefined): string {
  return (links ?? []).map((l) => `${l.href} | ${l.label}`).join("\n");
}
function textToLinks(text: string): SeoLink[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const i = line.indexOf("|");
      if (i === -1) return { href: line, label: line };
      return { href: line.slice(0, i).trim(), label: line.slice(i + 1).trim() };
    })
    .filter((l) => l.href && l.label);
}

export default function AdminBlogEditPage({ user }: { user: AuthUser }) {
  const t = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { toast } = useToast();
  const isEdit = !!id;

  const { data: allPosts } = useQuery(getAdminBlogPosts, { status: "ALL" });
  const { data: categories } = useQuery(getBlogCategories, undefined);

  const post = isEdit ? (allPosts ?? []).find((p: any) => p.id === id) : null;

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [categoryId, setCategoryId] = useState<string>("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [authorName, setAuthorName] = useState("Edgely Team");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [seoBodyH1, setSeoBodyH1] = useState("");
  const [seoBodySectionsText, setSeoBodySectionsText] = useState("");
  const [seoBodyLinksText, setSeoBodyLinksText] = useState("");
  const [coverImageS3Key, setCoverImageS3Key] = useState<string | undefined>(undefined);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setSlug(post.slug);
      setSlugManuallyEdited(true);
      setCategoryId(post.categoryId ?? "");
      setTags(post.tags ?? []);
      setAuthorName(post.authorName);
      setContent(post.content);
      setExcerpt(post.excerpt ?? "");
      setMetaTitle(post.metaTitle ?? "");
      setMetaDescription(post.metaDescription ?? "");
      setSeoBodyH1(post.seoBodyH1 ?? "");
      setSeoBodySectionsText(sectionsToText(post.seoBodySections));
      setSeoBodyLinksText(linksToText(post.seoBodyLinks));
      setCoverImageS3Key(post.coverImageS3Key ?? undefined);
    }
  }, [post?.id]);

  useEffect(() => {
    if (!slugManuallyEdited) {
      setSlug(toSlug(title));
    }
  }, [title, slugManuallyEdited]);

  const createPostAction = useAction(createBlogPost);
  const updatePostAction = useAction(updateBlogPost);
  const publishAction = useAction(publishBlogPost);
  const unpublishAction = useAction(unpublishBlogPost);
  const getUploadUrl = useAction(createFileUploadUrl);
  const addFile = useAction(addFileToDb);

  const handleImageUpload = async (file: File): Promise<{ s3Key: string; downloadUrl: string }> => {
    const ext = file.name.split(".").pop() ?? "jpg";
    const fileType = (file.type || "image/jpeg") as AllowedFileType;
    const fileName = `blog-inline-${Date.now()}.${ext}`;
    const { s3UploadUrl, s3UploadFields, s3Key } = await getUploadUrl({ fileType, fileName });
    const formData = new FormData();
    Object.entries(s3UploadFields).forEach(([k, v]) => formData.append(k, v as string));
    formData.append("file", file);
    await fetch(s3UploadUrl, { method: "POST", body: formData });
    await addFile({ s3Key, fileName, fileType });
    return { s3Key, downloadUrl: "" };
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop() ?? "jpg";
    const fileType = (file.type || "image/jpeg") as AllowedFileType;
    const fileName = `blog-cover-${Date.now()}.${ext}`;
    const { s3UploadUrl, s3UploadFields, s3Key } = await getUploadUrl({ fileType, fileName });
    const formData = new FormData();
    Object.entries(s3UploadFields).forEach(([k, v]) => formData.append(k, v as string));
    formData.append("file", file);
    await fetch(s3UploadUrl, { method: "POST", body: formData });
    await addFile({ s3Key, fileName, fileType });
    setCoverImageS3Key(s3Key);
    setCoverPreviewUrl(URL.createObjectURL(file));
  };

  const getPayload = () => ({
    title,
    slug,
    excerpt: excerpt || undefined,
    coverImageS3Key: coverImageS3Key || undefined,
    content,
    categoryId: categoryId || undefined,
    tags,
    authorName,
    metaTitle: metaTitle || undefined,
    metaDescription: metaDescription || undefined,
    seoBodyH1: seoBodyH1.trim() || null,
    seoBodySections: textToSections(seoBodySectionsText),
    seoBodyLinks: textToLinks(seoBodyLinksText),
  });

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      if (isEdit && id) {
        await updatePostAction({ id, ...getPayload() });
        toast({ title: t.adminBlog.postSaved });
      } else {
        const created = await createPostAction(getPayload());
        toast({ title: t.adminBlog.postSaved });
        navigate(`/admin/blog/${created.id}/edit`);
      }
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    setIsSaving(true);
    try {
      let targetId = id;
      if (!isEdit) {
        const created = await createPostAction(getPayload());
        targetId = created.id;
        navigate(`/admin/blog/${created.id}/edit`, { replace: true });
      } else {
        await updatePostAction({ id: id!, ...getPayload() });
      }
      await publishAction({ id: targetId! });
      toast({ title: t.adminBlog.postPublished });
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnpublish = async () => {
    if (!id) return;
    setIsSaving(true);
    try {
      await unpublishAction({ id });
      toast({ title: t.adminBlog.postUnpublished });
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!tags.includes(newTag)) setTags([...tags, newTag]);
      setTagInput("");
    }
  };

  const isPublished = post?.status === "PUBLISHED";

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName={isEdit ? t.adminBlog.editPost : t.adminBlog.createPost} />

      <div className="max-w-4xl space-y-6 pb-20">
        <div>
          <Label htmlFor="title">{t.adminBlog.titleField} *</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titre de l'article"
            className="mt-1 text-lg"
          />
        </div>

        <div>
          <Label htmlFor="slug">{t.adminBlog.slugField}</Label>
          <Input
            id="slug"
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setSlugManuallyEdited(true); }}
            placeholder="mon-article"
            className="mt-1 font-mono text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t.adminBlog.categoryField}</Label>
            <Select
              value={categoryId || "none"}
              onValueChange={(v) => setCategoryId(v === "none" ? "" : v)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t.adminBlog.noCategory} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t.adminBlog.noCategory}</SelectItem>
                {(categories ?? []).map((cat: any) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="author">{t.adminBlog.authorField}</Label>
            <Input
              id="author"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label>{t.adminBlog.tagsField}</Label>
          <div className="mt-1 flex flex-wrap gap-2 p-2 rounded-md border border-input bg-background min-h-[42px]">
            {tags.map((tag) => (
              <span key={tag} className="flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-0.5 rounded text-sm">
                {tag}
                <button onClick={() => setTags(tags.filter((tt) => tt !== tag))} className="hover:text-destructive">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              placeholder="Ajouter un tag (Entrée)"
              className="flex-1 min-w-24 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>

        <div>
          <Label>{t.adminBlog.coverImage}</Label>
          <div className="mt-1">
            {coverPreviewUrl || coverImageS3Key ? (
              <div className="relative w-full max-w-md">
                {coverPreviewUrl && (
                  <img src={coverPreviewUrl} alt="Cover" className="rounded-lg w-full h-48 object-cover" />
                )}
                {!coverPreviewUrl && coverImageS3Key && (
                  <div className="rounded-lg w-full h-48 bg-card flex items-center justify-center text-muted-foreground text-sm">
                    Image de couverture enregistrée
                  </div>
                )}
                <button
                  onClick={() => { setCoverImageS3Key(undefined); setCoverPreviewUrl(undefined); }}
                  className="absolute top-2 right-2 bg-background/80 rounded-full p-1 hover:bg-background"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 rounded-md border border-dashed border-border hover:border-primary/50 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Upload className="w-4 h-4" />
                Choisir une image
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
              </label>
            )}
          </div>
        </div>

        <div>
          <Label>Contenu</Label>
          <div className="mt-1 border border-input rounded-md overflow-hidden">
            <NotebookEditor
              content={content}
              onChange={setContent}
              onImageUpload={handleImageUpload}
            />
          </div>
        </div>

        <Accordion type="single" collapsible>
          <AccordionItem value="seo">
            <AccordionTrigger className="text-sm font-medium">{t.adminBlog.seoSection}</AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <div>
                <Label htmlFor="excerpt">{t.adminBlog.excerptField}</Label>
                <Textarea
                  id="excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Résumé court de l'article"
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="metaTitle">{t.adminBlog.metaTitle}</Label>
                <Input
                  id="metaTitle"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder={title || "Meta titre SEO"}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="metaDescription">{t.adminBlog.metaDescription}</Label>
                <Textarea
                  id="metaDescription"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder={excerpt || "Meta description SEO (max 160 caractères)"}
                  rows={2}
                  className="mt-1"
                />
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium">{t.adminBlog.seoBodyTitle}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.adminBlog.seoBodyHelp}</p>
              </div>
              <div>
                <Label htmlFor="seoBodyH1">{t.adminBlog.seoBodyH1}</Label>
                <Input
                  id="seoBodyH1"
                  value={seoBodyH1}
                  onChange={(e) => setSeoBodyH1(e.target.value)}
                  placeholder={metaTitle || title || "H1 SSR (défaut : le titre)"}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="seoBodySections">{t.adminBlog.seoBodySections}</Label>
                <p className="text-xs text-muted-foreground mt-1">{t.adminBlog.seoBodySectionsHelp}</p>
                <Textarea
                  id="seoBodySections"
                  value={seoBodySectionsText}
                  onChange={(e) => setSeoBodySectionsText(e.target.value)}
                  placeholder={"Pourquoi un journal est indispensable | Trader avec une prop firm…"}
                  rows={5}
                  className="mt-1 font-mono text-xs"
                />
              </div>
              <div>
                <Label htmlFor="seoBodyLinks">{t.adminBlog.seoBodyLinks}</Label>
                <p className="text-xs text-muted-foreground mt-1">{t.adminBlog.seoBodyLinksHelp}</p>
                <Textarea
                  id="seoBodyLinks"
                  value={seoBodyLinksText}
                  onChange={(e) => setSeoBodyLinksText(e.target.value)}
                  placeholder={"/tarifs | Voir les tarifs"}
                  rows={5}
                  className="mt-1 font-mono text-xs"
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur px-6 py-3 flex items-center justify-end gap-3">
        {isPublished ? (
          <>
            <Button variant="outline" onClick={handleUnpublish} disabled={isSaving}>
              {t.adminBlog.unpublish}
            </Button>
            <Button onClick={handlePublish} disabled={isSaving || !title}>
              {t.adminBlog.savePublished}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={handleSaveDraft} disabled={isSaving}>
              {t.adminBlog.saveDraft}
            </Button>
            <Button onClick={handlePublish} disabled={isSaving || !title}>
              {t.adminBlog.publish}
            </Button>
          </>
        )}
      </div>
    </DefaultLayout>
  );
}
